const getPeriodFromProgram = require('../../report/tools/get-period-from-program')

function checkForStockReportRateRequirements (pgConnection, programs) {
  if (!pgConnection) {
    const err = new Error('Postgres connection is required')
    err.status = 500
    throw err
  }

  if (!programs || (Array.isArray(programs) && programs.length === 0)) {
    const err = new Error('programs is required')
    err.status = 400
    throw err
  }
}

// The current reporting period (the one containing asOfDate), or null if the
// program's cycle can't be determined.
function getCurrentPeriod (programOptions, asOfDate = new Date()) {
  const periods = programOptions && programOptions.report && programOptions.report.periods
  if (!periods || !periods.length) {
    return null
  }

  const program = {
    id: 'temp',
    report: { periods: periods }
  }

  try {
    return getPeriodFromProgram(program, asOfDate, true)
  } catch (e) {
    return null
  }
}

// Obligation rows are pre-created a cycle ahead of their due date, so a query
// spanning multiple periods (e.g. a full year) must exclude the still-open
// current period, or every location's bool_and(submitted) drags to false for
// a cycle that hasn't had the chance to complete yet. The one exception is a
// query scoped entirely to the current period itself: there we want to show
// live, in-progress completion instead of hiding it as "no data".
function resolveIncompletePeriodExclusion ({ programOptions, fromDateUtc, toDateUtc, asOfDate = new Date() }) {
  const currentPeriod = getCurrentPeriod(programOptions, asOfDate)
  if (!currentPeriod) {
    return null
  }

  const queryIsScopedToCurrentPeriod = (
    fromDateUtc >= currentPeriod.effectiveStartDate &&
    toDateUtc <= currentPeriod.effectiveEndDate
  )
  if (queryIsScopedToCurrentPeriod) {
    return null
  }

  return currentPeriod.effectiveStartDate
}

function buildStockReportRateQueryContext ({ programs, fromDateUtc, toDateExclusiveUtc, excludeFromPeriodStart, country, zone, state, lga, sdp }) {
  const normalizedPrograms = programs
  const params = [
    normalizedPrograms,
    fromDateUtc.toISOString(),
    toDateExclusiveUtc.toISOString()
  ]

  if (excludeFromPeriodStart) {
    params.push(excludeFromPeriodStart.toISOString())
  }

  const whereClauses = []

  const scopeFilters = [
    ['country', country],
    ['zone', zone],
    ['state', state],
    ['lga', lga],
    ['sdp', sdp]
  ]

  scopeFilters.forEach(([column, value]) => {
    if (!value) {
      return
    }
    whereClauses.push(`AND srr.${column} = $${params.length + 1}`)
    params.push(value)
  })

  return {
    params,
    whereClauses,
    hasIncompletePeriodsFilter: !!excludeFromPeriodStart
  }
}

function buildStockReportRateExportQueryContext ({ programs, fromDateUtc, toDateUtc, location, funders, limit, offset }) {
  const params = [
    programs,
    fromDateUtc.toISOString().slice(0, 10),
    toDateUtc.toISOString().slice(0, 10)
  ]
  const whereClauses = []

  if (location && location !== 'national') {
    whereClauses.push(`AND srr.location_fsid LIKE $${params.length + 1} || '%'`)
    params.push(location)
  }

  if (Array.isArray(funders) && funders.length > 0) {
    whereClauses.push(`AND srr.funder = ANY($${params.length + 1}::text[])`)
    params.push(funders)
  }

  const limitParam = params.length + 1
  params.push(Number(limit) || 5000)
  const offsetParam = params.length + 1
  params.push(Number(offset) || 0)

  return {
    params,
    whereClauses,
    limitParam,
    offsetParam
  }
}

function buildStockReportRateSummaryQuery ({ whereClauses, hasIncompletePeriodsFilter }) {
  const incompletePeriodsClause = hasIncompletePeriodsFilter ? 'AND srr.period_start < $4::date' : ''
  return `
      WITH location_completion AS (
        SELECT
          srr.location_fsid,
          bool_and(srr.submitted = true) AS completed
        FROM analytics.stock_report_rate srr
        WHERE srr.program_fsid = ANY($1::text[])
          AND srr.period_start >= $2::date
          AND srr.period_start < $3::date
          ${incompletePeriodsClause}
          ${whereClauses.join('\n')}
        GROUP BY srr.location_fsid
      )
      SELECT
        count(*)::int AS expected_locations,
        count(*) FILTER (WHERE lc.completed = true)::int AS completed_locations
      FROM location_completion lc
    `
}

function buildStockReportRateLocationTableByAreaQuery ({ whereClauses, groupedByColumn, contextColumns, hasIncompletePeriodsFilter }) {
  const contextSelectSql = contextColumns.map((column) => `lc.${column}`).join(',\n        ')
  const contextGroupBySql = contextColumns.map((column) => `lc.${column}`).join(', ')
  const incompletePeriodsClause = hasIncompletePeriodsFilter ? 'AND srr.period_start < $4::date' : ''
  return `
      WITH filtered_obligations AS (
        SELECT
          srr.location_fsid,
          srr.country,
          srr.zone,
          srr.state,
          srr.lga,
          srr.sdp,
          srr.submitted
        FROM analytics.stock_report_rate srr
        WHERE srr.program_fsid = ANY($1::text[])
          AND srr.period_start >= $2::date
          AND srr.period_start < $3::date
          ${incompletePeriodsClause}
          ${whereClauses.join('\n')}
      ),
      location_completion AS (
        SELECT
          fo.location_fsid,
          fo.country,
          fo.zone,
          fo.state,
          fo.lga,
          fo.sdp,
          bool_and(fo.submitted = true) AS completed
        FROM filtered_obligations fo
        GROUP BY fo.location_fsid, fo.country, fo.zone, fo.state, fo.lga, fo.sdp
      )
      SELECT
        ${contextSelectSql},
        lc.${groupedByColumn} AS grouped_by_value,
        count(*)::int AS total_number_of_locations_expected_to_report,
        count(*) FILTER (WHERE lc.completed = true)::int AS number_of_locations_completed
      FROM location_completion lc
      WHERE lc.${groupedByColumn} IS NOT NULL
      GROUP BY ${contextGroupBySql}, lc.${groupedByColumn}
      ORDER BY lc.${groupedByColumn} ASC
    `
}

function buildStockReportRateLocationTableBySdpQuery ({ whereClauses, hasIncompletePeriodsFilter }) {
  const incompletePeriodsClause = hasIncompletePeriodsFilter ? 'AND srr.period_start < $4::date' : ''
  return `
      WITH filtered_obligations AS (
        SELECT
          srr.location_fsid,
          srr.sdp,
          -- this is not used in the query, but is needed for the result mapping
          -- so that we can return the stock report fsids for the expected and submitted stock reports
          srr.stock_report_fsid,
          -- end of comment
          srr.submitted
        FROM analytics.stock_report_rate srr
        WHERE srr.program_fsid = ANY($1::text[])
          AND srr.period_start >= $2::date
          AND srr.period_start < $3::date
          ${incompletePeriodsClause}
          ${whereClauses.join('\n')}
      )
      SELECT
        fo.location_fsid,
        fo.sdp,
        bool_and(fo.submitted = true) AS completed,
        count(*)::int AS number_of_reports_expected,
        count(*) FILTER (WHERE fo.submitted = true)::int AS number_of_reports_submitted,
        -- this is not used in the query, but is needed for the result mapping
        -- so that we can return the stock report fsids for the expected and submitted stock reports
        -- this functionality has been added to improve debugging of the indicator in the UI
        -- but adds to the loading time. It can be removed if needed
        COALESCE(
          array_agg(fo.stock_report_fsid) FILTER (
            WHERE fo.stock_report_fsid IS NOT NULL
          ),
          ARRAY[]::text[]
        ) AS expected_stock_report_ids,
        COALESCE(
          array_agg(fo.stock_report_fsid) FILTER (
            WHERE fo.submitted = true AND fo.stock_report_fsid IS NOT NULL
          ),
          ARRAY[]::text[]
        ) AS submitted_stock_report_ids
      -- end of comment
      FROM filtered_obligations fo
      GROUP BY fo.location_fsid, fo.sdp
      ORDER BY fo.sdp ASC, fo.location_fsid ASC
    `
}

function buildStockReportRateLocationTableQuery ({ whereClauses, groupedBy, hasIncompletePeriodsFilter }) {
  // At the SDP level, we show different data than at the other levels (lga, state, zone, national).
  // When grouped by sdp, we show the number of expected and submitted stock reports for each sdp
  // and the stock report fsids for the expected and submitted stock reports.
  // When grouped by other levels, we show the number of locations expected to report and the number of locations completed
  // without telling exactly which reports were expected and submitted, the only focus is completion.
  // For this reason, we have separate queries for the two cases.
  if (groupedBy === 'sdp') {
    return buildStockReportRateLocationTableBySdpQuery({ whereClauses, hasIncompletePeriodsFilter })
  }

  const groupedByConfig = {
    zone: {
      column: 'zone',
      contextColumns: ['country', 'zone']
    },
    state: {
      column: 'state',
      contextColumns: ['country', 'zone', 'state']
    },
    lga: {
      column: 'lga',
      contextColumns: ['country', 'zone', 'state', 'lga']
    }
  }[groupedBy]
  if (!groupedByConfig) {
    const err = new Error('groupedBy must be one of zone, state, lga, sdp')
    err.status = 400
    throw err
  }
  return buildStockReportRateLocationTableByAreaQuery({
    whereClauses,
    groupedByColumn: groupedByConfig.column,
    contextColumns: groupedByConfig.contextColumns,
    hasIncompletePeriodsFilter
  })
}

function buildStockReportRateCycleByCycleQuery ({ whereClauses, limit, hasIncompletePeriodsFilter }) {
  const incompletePeriodsClause = hasIncompletePeriodsFilter ? 'AND srr.period_start < $4::date' : ''
  return `
      WITH filtered_obligations AS (
        SELECT
          srr.period_start,
          srr.location_fsid,
          srr.submitted
        FROM analytics.stock_report_rate srr
        WHERE srr.program_fsid = ANY($1::text[])
          AND srr.period_start >= $2::date
          AND srr.period_start < $3::date
          ${incompletePeriodsClause}
          ${whereClauses.join('\n')}
      ),
      location_completion AS (
        SELECT
          fo.period_start,
          fo.location_fsid,
          bool_and(fo.submitted = true) AS completed
        FROM filtered_obligations fo
        GROUP BY fo.period_start, fo.location_fsid
      ),
      cycle_summary AS (
        SELECT
          lc.period_start,
          count(*)::int AS expected_locations,
          count(*) FILTER (WHERE lc.completed = true)::int AS completed_locations
        FROM location_completion lc
        GROUP BY lc.period_start
      )
      SELECT
        cs.period_start::date::text AS period_start_date,
        cs.expected_locations,
        cs.completed_locations
      FROM cycle_summary cs
      ORDER BY cs.period_start DESC
      LIMIT ${Number(limit) || 60}
    `
}

function buildStockReportRateProgramCyclesQuery () {
  return `
      SELECT
        dp.fsid,
        dp.options
      FROM avocado.data_program dp
      WHERE dp.fsid = ANY($1::text[])
    `
}

function buildStockReportRateExportRowsQuery ({ whereClauses, limitParam, offsetParam }) {
  return `
      SELECT
        srr.period_start::date::text AS date,
        srr.location_fsid AS location_id,
        srr.zone,
        srr.state,
        srr.lga,
        srr.sdp,
        srr.warehouse_code,
        srr.program_fsid,
        srr.service_fsid,
        srr.funder,
        srr.submitted,
        srr.submitted_at
      FROM analytics.stock_report_rate srr
      WHERE srr.program_fsid = ANY($1::text[])
        AND srr.period_start >= $2::date
        AND srr.period_start <= $3::date
        ${whereClauses.join('\n')}
      ORDER BY srr.period_start ASC, srr.location_fsid ASC, srr.service_fsid ASC
      LIMIT $${limitParam}::int
      OFFSET $${offsetParam}::int
    `
}

// These values match should match those in getLevel in backend/stacks/internal_api/endpoint/src/transformers.js
const STOCK_REPORT_RATE_THRESHOLDS = {
  ok: 0.9,
  warning: 0.7
}

function getStockReportRateLevel (value) {
  if (value === null || Number.isNaN(value)) {
    return 'noData'
  }
  if (value > STOCK_REPORT_RATE_THRESHOLDS.ok) {
    return 'ok'
  }
  if (value > STOCK_REPORT_RATE_THRESHOLDS.warning) {
    return 'warning'
  }
  return 'alert'
}

module.exports = {
  checkForStockReportRateRequirements,
  getCurrentPeriod,
  resolveIncompletePeriodExclusion,
  buildStockReportRateQueryContext,
  buildStockReportRateExportQueryContext,
  buildStockReportRateSummaryQuery,
  buildStockReportRateLocationTableQuery,
  buildStockReportRateCycleByCycleQuery,
  buildStockReportRateProgramCyclesQuery,
  buildStockReportRateExportRowsQuery,
  getStockReportRateLevel
}
